import { BaseSyntheticEvent, MouseEvent, useCallback, useMemo } from 'react';
import {
  FieldValues,
  useFormContext,
  FieldErrors,
  useFormState,
} from 'react-hook-form';
import { Button, ButtonProps } from 'antd';
import { useFormSubmitHandler } from './AdditionalFormContext';
import { useFormReset } from './useResetForm';
import { ValidationMessagesMap } from '../utils/ValidationMessagesMap';

export interface FormSubmitButtonProps<FormData extends FieldValues>
  extends Omit<ButtonProps, 'id' | 'onSubmit'> {
  id?: string;
  onValidationFailed?: (
    errors: FieldErrors<FormData>,
    values: FormData,
    event?: BaseSyntheticEvent,
  ) => unknown | Promise<unknown>;
  buttonType?: ButtonProps['type'];
}

export const FormSubmitButton = <FormData extends FieldValues>(
  props: FormSubmitButtonProps<FormData>,
) => {
  const {
    id = 'formSubmit',
    onValidationFailed,
    loading,
    onClick,
    buttonType,
    ...rest
  } = props;
  const form = useFormContext<FormData>();
  const {
    formState: { isSubmitting },
  } = form;
  const formSubmitHandler = useFormSubmitHandler<FormData>();
  const resetForm = useFormReset<FormData>();

  const { errors } = useFormState<FormData>();
  const validationMessagesMap = useMemo(
    () => ValidationMessagesMap.fromFieldErrors(errors),
    [errors],
  );
  const disableButton =
    validationMessagesMap.totalWarnings > 0 &&
    validationMessagesMap.totalErrors === 0;

  const handleSubmit = useCallback(
    (data: FormData, event?: BaseSyntheticEvent) =>
      resetForm(data, formSubmitHandler(data, false, event)),
    [formSubmitHandler, resetForm],
  );

  const submit = useMemo(() => {
    const submissionHandler = form.handleSubmit(
      handleSubmit,
      (errors, event) => {
        const values = form.getValues();
        onValidationFailed?.(errors, values, event);
        if (import.meta.env.DEV) {
          console.error('Form validation failed', { errors, values, event });
        }
      },
    );
    if (!onClick) {
      return submissionHandler;
    }
    return (event: MouseEvent<HTMLButtonElement>) => {
      onClick(event);
      return submissionHandler(event);
    };
  }, [form, handleSubmit, onValidationFailed, onClick]);

  return (
    <Button
      {...rest}
      id={id}
      type={buttonType || 'primary'}
      onClick={submit}
      loading={(loading || isSubmitting) && { delay: 200 }}
      disabled={disableButton}
    />
  );
};
